import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { LayoutGrid, Play, Columns, Users, Zap } from "lucide-react";

const STAGES = ["Awareness", "Knowledge", "Liking", "Preference", "Conviction", "Purchase"];

const INPUT_CARDS = [
  { label: "Product", icon: LayoutGrid, color: "bg-violet-500/10 text-violet-400 border-violet-500/20" },
  { label: "Persona", icon: Users, color: "bg-sky-500/10 text-sky-400 border-sky-500/20" },
  { label: "Narrative Opportunity", icon: Zap, color: "bg-indigo-500/10 text-indigo-400 border-indigo-500/20" },
  { label: "Narrative Angles", icon: Columns, color: "bg-cyan-500/10 text-cyan-400 border-cyan-500/20" },
];

const MEDIA_TYPES = [
  { label: "Static Ads", dot: "bg-sky-400" },
  { label: "Video Ads", dot: "bg-violet-400" },
  { label: "Carousel Ads", dot: "bg-indigo-400" },
  { label: "UGC Style", dot: "bg-emerald-400" },
  { label: "Scroll Stopper", dot: "bg-amber-400" },
];

const FUNNEL_COUNTS = [8, 7, 8, 5, 6, 3];

function StepHeader({ step, title, subtitle }: { step: number; title: string; subtitle: string }) {
  return (
    <motion.div
      key={title}
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.4 }}
      className="text-center space-y-1.5 mb-8"
    >
      <div className="flex items-center justify-center gap-2 mb-3">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-blue-300">
          Step {step} of 5
        </span>
        <span className="w-1 h-1 rounded-full bg-blue-400/40" />
        <div className="h-px bg-blue-500/20 w-16" />
      </div>
      <h3 className="text-lg font-bold text-white tracking-tight">{title}</h3>
      <p className="text-sm text-blue-200/70">{subtitle}</p>
    </motion.div>
  );
}

function Step1() {
  const [visibleCards, setVisibleCards] = useState<number[]>([]);

  useEffect(() => {
    INPUT_CARDS.forEach((_, i) => {
      setTimeout(() => setVisibleCards((prev) => [...prev, i]), 600 * i + 200);
    });
  }, []);

  return (
    <div className="space-y-3">
      {INPUT_CARDS.map((card, i) => {
        const Icon = card.icon;
        return (
          <AnimatePresence key={card.label}>
            {visibleCards.includes(i) && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                className={`flex items-center gap-3 rounded-xl border px-4 py-3 ${card.color}`}
              >
                <Icon className="w-4 h-4 shrink-0" />
                <span className="text-sm font-medium text-white/90">{card.label}</span>
                <motion.div
                  className="ml-auto h-1.5 rounded-full bg-current opacity-40"
                  initial={{ width: 0 }}
                  animate={{ width: "60%" }}
                  transition={{ delay: 0.3, duration: 1.2, ease: "easeOut" }}
                />
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 1.5, duration: 0.2 }}
                  className="w-2 h-2 rounded-full bg-current shrink-0"
                />
              </motion.div>
            )}
          </AnimatePresence>
        );
      })}

      {visibleCards.length === INPUT_CARDS.length && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="flex items-center justify-center gap-2 pt-2"
        >
          {INPUT_CARDS.map((_, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-blue-400/60 animate-pulse" style={{ animationDelay: `${i * 0.3}s` }} />
              {i < INPUT_CARDS.length - 1 && <div className="w-8 h-px bg-blue-400/30" />}
            </div>
          ))}
        </motion.div>
      )}
    </div>
  );
}

function Step2() {
  const [activeStage, setActiveStage] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStage((prev) => (prev + 1) % STAGES.length);
    }, 700);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="space-y-1.5">
      {STAGES.map((stage, i) => (
        <motion.div
          key={stage}
          className={`flex items-center gap-3 rounded-xl px-4 py-2.5 transition-all duration-500 ${
            i === activeStage
              ? "bg-blue-500/20 border border-blue-400/30"
              : "bg-white/4 border border-transparent"
          }`}
        >
          <span
            className={`text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center shrink-0 transition-colors duration-500 ${
              i === activeStage ? "bg-blue-400 text-black" : "bg-white/10 text-white/40"
            }`}
          >
            {i + 1}
          </span>
          <span
            className={`text-sm font-medium transition-colors duration-500 ${
              i === activeStage ? "text-white" : "text-white/40"
            }`}
          >
            {stage}
          </span>
          <div className="ml-auto flex items-center gap-1">
            {i === activeStage &&
              Array.from({ length: 4 }).map((_, j) => (
                <motion.div
                  key={j}
                  className="w-1.5 h-1.5 rounded-full bg-blue-400"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: j * 0.12, duration: 0.2 }}
                />
              ))}
          </div>
        </motion.div>
      ))}
    </div>
  );
}

function Step3() {
  return (
    <div className="relative min-h-[220px] flex flex-col items-center justify-center gap-6">
      <div className="flex flex-wrap justify-center gap-3">
        {MEDIA_TYPES.map((type, i) => (
          <motion.div
            key={type.label}
            className="flex items-center gap-2 rounded-full bg-white/8 border border-white/10 px-3 py-1.5"
            animate={{
              y: [0, -6 - (i % 3) * 3, 0, 4 + (i % 2) * 2, 0],
              opacity: [0.7, 1, 0.8, 1, 0.7],
            }}
            transition={{
              duration: 3 + i * 0.7,
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 0.4,
            }}
          >
            <div className={`w-2 h-2 rounded-full ${type.dot}`} />
            <span className="text-xs font-medium text-white/80">{type.label}</span>
          </motion.div>
        ))}
      </div>

      <div className="w-full space-y-1.5">
        {STAGES.map((stage, i) => (
          <div key={stage} className="flex items-center gap-3">
            <span className="text-[10px] text-white/40 w-24 shrink-0">{stage}</span>
            <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
              <motion.div
                className="h-full rounded-full bg-gradient-to-r from-sky-400 to-violet-400"
                animate={{ width: ["0%", `${35 + i * 10}%`, "0%"] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: i * 0.3 }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CountUp({ target, delay = 0 }: { target: number; delay?: number }) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const duration = 1200;
      const steps = target;
      const interval = duration / steps;
      let current = 0;
      const timer = setInterval(() => {
        current += 1;
        setValue(current);
        if (current >= target) clearInterval(timer);
      }, interval);
      return () => clearInterval(timer);
    }, delay);
    return () => clearTimeout(timeout);
  }, [target, delay]);

  return <span>{value}</span>;
}

function Step4() {
  return (
    <div className="space-y-2">
      {STAGES.map((stage, i) => (
        <div key={stage} className="flex items-center gap-3 rounded-xl bg-white/4 border border-white/8 px-4 py-2.5">
          <span className="text-sm text-white/70 w-28 shrink-0">{stage}</span>
          <div className="flex-1 flex items-center gap-1">
            {Array.from({ length: FUNNEL_COUNTS[i] }).map((_, j) => (
              <motion.div
                key={j}
                className="w-5 h-5 rounded bg-blue-500/20 border border-blue-400/20 flex items-center justify-center"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: i * 0.25 + j * 0.12, duration: 0.2 }}
              >
                <div className="w-1.5 h-1.5 rounded-full bg-blue-400" />
              </motion.div>
            ))}
          </div>
          <span className="text-sm font-bold text-blue-300 w-6 text-right">
            <CountUp target={FUNNEL_COUNTS[i]} delay={i * 250} />
          </span>
        </div>
      ))}
    </div>
  );
}

function Step5() {
  const [pulse, setPulse] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setPulse((p) => (p + 1) % STAGES.length);
    }, 900);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex items-start gap-8">
      {/* Radar */}
      <div className="relative w-28 h-28 shrink-0 flex items-center justify-center">
        <div className="absolute inset-0 rounded-full border border-blue-400/20" />
        <div className="absolute inset-3 rounded-full border border-blue-400/15" />
        <div className="absolute inset-6 rounded-full border border-blue-400/10" />
        <motion.div
          className="absolute inset-0 rounded-full"
          style={{
            background: "conic-gradient(from 0deg, transparent 70%, rgba(59,130,246,0.3) 100%)",
          }}
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        />
        <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
      </div>

      {/* Funnel nodes */}
      <div className="flex-1 space-y-2">
        {STAGES.map((stage, i) => (
          <div key={stage} className="flex items-center gap-2.5">
            <motion.div
              className="w-2 h-2 rounded-full shrink-0"
              animate={{
                backgroundColor: i === pulse ? "#60a5fa" : "rgba(255,255,255,0.15)",
                scale: i === pulse ? 1.4 : 1,
              }}
              transition={{ duration: 0.3 }}
            />
            <span className={`text-xs font-medium transition-colors duration-300 ${i === pulse ? "text-white" : "text-white/35"}`}>
              {stage}
            </span>
            {i === pulse && (
              <motion.span
                initial={{ opacity: 0, x: -4 }}
                animate={{ opacity: 1, x: 0 }}
                className="text-[10px] text-blue-300"
              >
                optimizing...
              </motion.span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const STEPS = [
  { step: 1, title: "Analyzing Campaign Inputs", subtitle: "Understanding campaign context and narrative inputs" },
  { step: 2, title: "Mapping Funnel Strategy", subtitle: "Aligning messaging strategy across funnel stages" },
  { step: 3, title: "Simulating Media Mix", subtitle: "Evaluating optimal media distribution" },
  { step: 4, title: "Allocating Creative Across Funnel", subtitle: "Modeling creative volume and testing strategy" },
  { step: 5, title: "Finalizing Media Allocation Report", subtitle: "Finalizing campaign allocation strategy" },
];

export function MediaAllocationLoading() {
  const [currentStep, setCurrentStep] = useState(1);
  const stepRef = useRef(1);

  useEffect(() => {
    const advance = (step: number) => {
      if (step >= 5) return;
      const timer = setTimeout(() => {
        stepRef.current = step + 1;
        setCurrentStep(step + 1);
        advance(step + 1);
      }, 5000);
      return timer;
    };

    const timer = advance(1);
    return () => { if (timer) clearTimeout(timer); };
  }, []);

  const meta = STEPS[currentStep - 1];

  return (
    <div
      className="relative rounded-2xl overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #0f2460 0%, #1a3a8a 50%, #1a3372 100%)",
        backgroundSize: "250% 250%",
        animation: "gradient-shift 10s ease infinite",
      }}
    >
      {/* Dot grid */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.08) 1px, transparent 1px)",
          backgroundSize: "18px 18px",
        }}
      />
      {/* Scan shimmer */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute top-0 bottom-0 w-10"
          style={{
            background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.04), transparent)",
            animation: "scan-shimmer 5s ease-in-out infinite",
          }}
        />
      </div>

      <div className="relative px-6 py-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            <StepHeader step={meta.step} title={meta.title} subtitle={meta.subtitle} />
            {currentStep === 1 && <Step1 />}
            {currentStep === 2 && <Step2 />}
            {currentStep === 3 && <Step3 />}
            {currentStep === 4 && <Step4 />}
            {currentStep === 5 && <Step5 />}
          </motion.div>
        </AnimatePresence>

        {/* Step progress dots */}
        <div className="flex items-center justify-center gap-2 mt-8">
          {STEPS.map((s) => (
            <div
              key={s.step}
              className={`rounded-full transition-all duration-500 ${
                s.step === currentStep
                  ? "w-5 h-1.5 bg-blue-400"
                  : s.step < currentStep
                  ? "w-1.5 h-1.5 bg-blue-400/60"
                  : "w-1.5 h-1.5 bg-white/15"
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
